<template>
  <div id="natcon-member-payment">
    <v-overlay :value="ProgressLoadingFlag">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <div class="header-content pa-8">
          <v-row align="center" justify="space-between">
            <!-- Left Side: Title and Icon -->
            <v-col cols="12" md="8" class="d-flex align-center">
              <div class="header-icon-box">
                <v-icon size="40px" color="white" class="mdi">mdi-earth</v-icon>
              </div>
              <div class="ml-4">
                <h1 class="header-title white--text mb-0">{{ PageTitle }}</h1>
                <p class="header-subtitle white--text mb-0">{{ PageDescription }}</p>
              </div>
            </v-col>

            <!-- Right Side: Action Buttons -->
            <v-col cols="12" md="4" class="text-right">
              <v-btn icon class="action-btn mr-3" @click.prevent="$router.go(-1)">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-btn icon class="action-btn" @click.prevent="refreshPageData" :loading="LoadingFlag">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-col>
          </v-row>
        </div>
      </div>
    </v-card>

    <v-card-text class="page-content">
      <v-dialog v-model="addNonMemberRecordPrompt" persistent max-width="75%">
        <add-sma-non-member pageTitle="Add Participant details" pageDescription="Add non member details"
          :showDialog="addNonMemberRecordPrompt" @hideDialog="addNewNonMember($event)" v-if="addNonMemberRecordPrompt">
        </add-sma-non-member>
      </v-dialog>
      <!-- Required Fields Note -->
      <v-card class="required-note-card mb-6" elevation="0" v-if="!StartupLoadingFlag">
        <div class="required-note-content">
          <div class="required-note-icon">
            <v-icon color="white" size="24">mdi-alert-circle</v-icon>
          </div>
          <div class="required-note-text">
            <p class="required-note-label mb-0">
              <span class="asterisk">*</span> indicates required field
            </p>
          </div>
        </div>
      </v-card>

      <!-- Loading State -->
      <div v-if="LoadingFlag" class="text-center py-8">
        <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
      </div>

      <!-- Skeleton Loader -->
      <v-container v-if="StartupLoadingFlag">
        <v-row>
          <v-col cols="12" md="4" v-for="n in 6" :key="n">
            <v-skeleton-loader type="article" elevation="2"></v-skeleton-loader>
          </v-col>
        </v-row>
      </v-container>

      <v-container v-if="!StartupLoadingFlag" class="px-0">
        <v-form ref="form2" v-model="valid1" lazy-validation>
          <!-- Registration Type and Member Selection Card -->
          <v-card class="form-card mb-6 animate-slide-up" elevation="3" rounded="lg">
            <v-card-title class="section-header bg-gradient-navy white--text">
              <v-icon left color="white">mdi-account-check</v-icon>
              Registration Details
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" md="4">
                  <label class="field-label">
                    <v-icon small color="#3A67B1" class="mr-1">mdi-account-badge</v-icon>
                    <span class="error--text">*</span> Member Type
                  </label>
                  <v-select v-model="MemberType" :items="MemberTypeOptions" :rules="MemberTypeRules"
                    :reduce="(option) => option.value" required outlined dense
                    prepend-inner-icon="mdi-account-badge"></v-select>
                </v-col>

                <v-col cols="12" md="8" v-if="MemberType == 2">
                  <label class="field-label">
                    <v-icon small color="#0097D7" class="mr-1">mdi-account-search</v-icon>
                    <span class="error--text">*</span> Select Member
                  </label>
                  <v-autocomplete @change="getSelectedMemberDetails" :reduce="(option) => option.value"
                    :loading="MemberIdOptionsLoading" :items="MemberIdOptions" :rules="MemberIdRules" v-model="MemberId"
                    required outlined dense clearable prepend-inner-icon="mdi-account-search">
                    <template v-slot:selection="{ item }">
                      <v-avatar size="32" class="mr-2">
                        <img :src="item.photo" :alt="item.text" />
                      </v-avatar>
                      {{ item.text }}
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-avatar>
                        <img :src="item.photo" :alt="item.text" />
                      </v-list-item-avatar>
                      <v-list-item-content>
                        <v-list-item-title>{{ item.text }}</v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <br />

          <!-- LOM and Member Information Cards -->
          <v-row class="mb-6" v-if="MemberType != '' && selectedMember.MemberId > 0">
            <!-- Selected Participant Card -->
            <v-col cols="12" md="6">
              <v-card class="info-card-jci h-100 animate-slide-up" elevation="3" rounded="lg">
                <v-card-title class="section-header bg-gradient-gold white--text">
                  <v-icon left color="white">mdi-account-circle</v-icon>
                  Selected Participant
                </v-card-title>
                <v-card-text class="pa-4 text-center">
                  <v-avatar v-if="selectedMember.MemberImagePath != ''" size="80" class="mb-3 elevation-3">
                    <v-img :src="selectedMember.MemberImagePath" :alt="selectedMember.MemberName"
                      lazy-src="/no-image-available.png">
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                  </v-avatar>
                  <div class="font-weight-bold mb-2">
                    {{ selectedMember.MemberName }}
                  </div>
                  <v-chip small color="warning" text-color="white">
                    {{ selectedMember.MembershipId }}
                  </v-chip>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Contact Information Card -->
            <v-col cols="12" md="6">
              <v-card class="form-card h-100 animate-slide-up" elevation="3" rounded="lg">
                <v-card-title class="section-header bg-gradient-navy white--text">
                  <v-icon left color="white">mdi-phone</v-icon>
                  Contact Information
                </v-card-title>
                <v-card-text class="pa-6">
                  <v-row>
                    <v-col cols="12">
                      <label class="field-label">
                        <v-icon small color="#3A67B1" class="mr-1">mdi-cellphone</v-icon>
                        <span class="error--text">*</span> Mobile Number
                      </label>
                      <v-text-field v-model="selectedMember.MobileNo" :rules="MobileNoRules" :counter="10"
                        v-mask="'##########'" type="number" placeholder="Enter mobile number" dense outlined
                        prepend-inner-icon="mdi-cellphone"></v-text-field>
                    </v-col>

                    <v-col cols="12">
                      <label class="field-label">
                        <v-icon small color="#0097D7" class="mr-1">mdi-email</v-icon>
                        <span class="error--text">*</span> Email Address
                      </label>
                      <v-text-field v-model="selectedMember.EmailId" :rules="EmailIdRules" type="email"
                        placeholder="Enter email address" dense outlined prepend-inner-icon="mdi-email"></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Personal Details Card -->
          <v-card class="form-card mb-6 animate-slide-up" elevation="3" rounded="lg"
            v-if="MemberType != '' && selectedMember.MemberId > 0">
            <v-card-title class="section-header bg-gradient-aqua white--text">
              <v-icon left color="white">mdi-account-details</v-icon>
              Personal Details
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <v-col cols="12" sm="6" md="3">
                  <label class="field-label">
                    <v-icon small color="#56BDA3" class="mr-1">mdi-gender-male-female</v-icon>
                    <span class="error--text">*</span> Gender
                  </label>
                  <v-select v-model="selectedMember.Gender" :items="GenderOptions" :rules="GenderRules" outlined dense
                    prepend-inner-icon="mdi-gender-male-female"></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3">
                  <label class="field-label">
                    <v-icon small color="#EDBE38" class="mr-1">mdi-food</v-icon>
                    <span class="error--text">*</span> Food preference
                  </label>
                  <v-select v-model="selectedMember.FoodChoice" :items="FoodPreferenceOptions"
                    :rules="FoodPreferenceRules" outlined dense prepend-inner-icon="mdi-food"></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" v-if="AspacFlag">
                  <label class="field-label">
                    <v-icon small color="#3A67B1" class="mr-1">mdi-tshirt-crew</v-icon>
                    <span class="error--text">*</span> T-shirt Size
                  </label>
                  <v-select v-model="TshirtSize" :items="TshirtSizeOptions" :rules="TshirtSizeRules" outlined dense
                    prepend-inner-icon="mdi-tshirt-crew"></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="3" v-if="AspacFlag">
                  <label class="field-label">
                    <v-icon small color="#0097D7" class="mr-1">mdi-star-outline</v-icon>
                    <span class="error--text">*</span> Are you attending this event for the first time?
                  </label>
                  <v-select v-model="FirstTimer" :items="FirstTimerOptions" :rules="FirstTimerRules" outlined dense
                    prepend-inner-icon="mdi-star-outline"></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- Professional & Medical Information Card -->
          <v-card class="form-card mb-6 animate-slide-up" elevation="3" rounded="lg"
            v-if="MemberType != '' && selectedMember.MemberId > 0">
            <v-card-title class="section-header bg-gradient-seaform white--text">
              <v-icon left color="white">mdi-briefcase-account</v-icon>
              Professional & Medical Information
            </v-card-title>
            <v-card-text class="pa-6">
              <v-row>
                <!-- Professional Information -->
                <v-col cols="12">
                  <div class="section-subtitle-jci mb-3">
                    <v-icon small color="#56BDA3" class="mr-2">mdi-office-building</v-icon>
                    <span class="font-weight-bold text-subtitle-2" style="color: #0A0F29;">Professional Details</span>
                  </div>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <label class="field-label">
                    <v-icon small color="#3A67B1" class="mr-1">mdi-domain</v-icon>
                    <span class="error--text">*</span> Company/Organization
                  </label>
                  <v-text-field v-model="CompanyName" :rules="CompanyNameRules" placeholder="Enter company name" dense
                    outlined prepend-inner-icon="mdi-domain"></v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <label class="field-label">
                    <v-icon small color="#0097D7" class="mr-1">mdi-office-building-outline</v-icon>
                    <span class="error--text">*</span> Department/Section
                  </label>
                  <v-text-field v-model="Department" :rules="DepartmentRules" placeholder="Enter department" dense
                    outlined prepend-inner-icon="mdi-office-building-outline"></v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <label class="field-label">
                    <v-icon small color="#56BDA3" class="mr-1">mdi-badge-account-horizontal</v-icon>
                    <span class="error--text">*</span> Job Title
                  </label>
                  <v-text-field v-model="JobTitle" :rules="JobTitleRules" placeholder="Enter job title" dense outlined
                    prepend-inner-icon="mdi-badge-account-horizontal"></v-text-field>
                </v-col>

                <!-- Additional Fields -->
                <v-col cols="12" sm="6" md="4" v-if="AspacFlag">
                  <label class="field-label">
                    <v-icon small color="#EDBE38" class="mr-1">mdi-passport</v-icon>
                    <span class="error--text">*</span> Passport Number
                  </label>
                  <v-text-field v-model="PassportNumber" :rules="PassportNumberRules"
                    placeholder="Enter passport number" dense outlined prepend-inner-icon="mdi-passport"></v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="4" v-if="AspacFlag">
                  <label class="field-label">
                    <v-icon small color="#3A67B1" class="mr-1">mdi-account-star</v-icon>
                    Senator Number
                    <span class="text-caption">(optional)</span>
                  </label>
                  <v-text-field v-model="SenatorNumber" :rules="SenatorNumberRules" placeholder="Enter senator number"
                    dense outlined prepend-inner-icon="mdi-account-star"></v-text-field>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <label class="field-label">
                    <v-icon small color="#0097D7" class="mr-1">mdi-email-multiple</v-icon>
                    <span class="error--text">*</span> JVC Email ID
                  </label>
                  <v-text-field v-model="JvcEmailId" :rules="JvcEmailIdRules" type="email" placeholder="Enter JVC email"
                    dense outlined prepend-inner-icon="mdi-email-multiple"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-sheet elevation="6" rounded="xl" class="pa-6 mb-6 jvc-premium-note animate-slide-up"
                    style="background: linear-gradient(135deg, #3A67B1 0%, #1E3A8A 100%); border-left: 8px solid #EDBE38; position: relative; overflow: hidden;">
                    <!-- Decorative Background Icon -->
                    <div style="position: absolute; top: -20px; right: -20px; opacity: 0.1; transform: rotate(15deg);">
                      <v-icon size="160" color="white">mdi-earth</v-icon>
                    </div>

                    <div class="d-flex align-start" style="position: relative; z-index: 1;">
                      <v-avatar color="rgba(237, 190, 56, 0.2)" size="64" class="mr-5 elevation-2">
                        <v-icon color="#EDBE38" size="36">mdi-shield-check</v-icon>
                      </v-avatar>
                      <div>
                        <div class="text-h5 font-weight-bold white--text mb-2 d-flex align-center flex-wrap">
                          JVC Registration Mandatory
                          <v-chip color="#EDBE38" text-color="#0A0F29" small class="ml-3 font-weight-black elevation-2">
                            REQUIRED
                          </v-chip>
                        </div>
                        <p class="text-body-1 white--text mb-0"
                          style="line-height: 1.6; opacity: 0.95; max-width: 95%;">
                          Registration in <span class="font-weight-bold" style="color: #EDBE38;">JVC</span> is mandatory
                          for all
                          participants.
                          Please enter your registered JVC Email ID carefully below to continue with your
                          <span class="font-weight-bold">World Congress</span> registration process.
                        </p>
                      </div>
                    </div>
                  </v-sheet>
                </v-col>

                <!-- GST Section -->
                <v-col cols="12">
                  <v-divider class="my-4"></v-divider>
                </v-col>

                <v-col cols="12" sm="6" md="4">
                  <label class="field-label">
                    <v-icon small color="#56BDA3" class="mr-1">mdi-receipt</v-icon>
                    Do you want GST Bill
                    <span class="text-caption">(optional)</span>
                  </label>
                  <v-select v-model="selectedMember.GstNumberFlag" :items="GstNumberFlagOptions" outlined dense
                    prepend-inner-icon="mdi-receipt"></v-select>
                </v-col>

                <v-col cols="12" sm="6" md="4" v-if="selectedMember.GstNumberFlag == 1">
                  <label class="field-label">
                    <v-icon small color="#EDBE38" class="mr-1">mdi-file-document</v-icon>
                    <span class="error--text">*</span> GST Number
                  </label>
                  <v-text-field v-model="selectedMember.GstNumber" :rules="GstNumberRules"
                    placeholder="Enter GST number" dense outlined prepend-inner-icon="mdi-file-document"></v-text-field>
                </v-col>

                <!-- Medical Information -->
                <v-col cols="12">
                  <v-divider class="my-4"></v-divider>
                  <div class="section-subtitle-jci mb-3">
                    <v-icon small color="#EDBE38" class="mr-2">mdi-medical-bag</v-icon>
                    <span class="font-weight-bold text-subtitle-2" style="color: #0A0F29;">Medical Information</span>
                  </div>
                </v-col>

                <v-col cols="12" sm="6">
                  <label class="field-label">
                    <v-icon small color="#ef4444" class="mr-1">mdi-alert-circle-outline</v-icon>
                    <span class="error--text">*</span> Any known Allergies
                  </label>
                  <v-textarea v-model="Allergies" :rules="AllergiesRules" placeholder="Enter allergies or 'None'" dense
                    outlined rows="3" prepend-inner-icon="mdi-alert-circle-outline"></v-textarea>
                </v-col>

                <v-col cols="12" sm="6">
                  <label class="field-label">
                    <v-icon small color="#dc2626" class="mr-1">mdi-heart-pulse</v-icon>
                    <span class="error--text">*</span> Medical Conditions
                  </label>
                  <v-textarea v-model="MedicalConditions" :rules="MedicalConditionsRules"
                    placeholder="Enter medical conditions or 'None'" dense outlined rows="3"
                    prepend-inner-icon="mdi-heart-pulse"></v-textarea>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
          <br />

          <!-- Participant Details Card (For Non-Members) -->
          <v-card class="form-card mb-6 animate-slide-up" elevation="3" rounded="lg" v-if="MemberType == 3">
            <v-card-title class="section-header bg-gradient-navy white--text">
              <v-icon left color="white">mdi-account-group</v-icon>
              Participant Details
            </v-card-title>
            <v-card-text class="pa-6">
              <v-data-table :headers="tableColumns2" :items="AllParticipants" hide-default-footer disable-sort
                class="elevation-1 custom-table-jci">
                <template v-slot:no-data>
                  <div class="text-center pa-4">
                    <p class="mb-3">No participants added yet</p>
                    <v-btn color="primary" @click.prevent="addNonMemberRecordPrompt = true">
                      <v-icon left>mdi-plus-circle</v-icon>
                      Add Participant
                    </v-btn>
                  </div>
                </template>
                <template v-slot:item.action="{ item, index }">
                  <v-btn icon color="primary" @click.prevent="openNonMemberDialog">
                    <v-icon>mdi-plus-circle</v-icon>
                  </v-btn>
                  <v-btn icon color="error" @click="deleteRow(1, index, item)">
                    <v-icon>mdi-delete-circle-outline</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>

          <!-- Passport Details Card -->
          <v-card class="form-card mb-6 animate-slide-up" elevation="3" rounded="lg" v-if="selectedMember.MemberId > 0">
            <v-card-title class="section-header bg-gradient-aqua white--text">
              <v-icon left color="white">mdi-file-document</v-icon>
              <div>
                <div>Passport Details</div>
                <div class="text-caption font-weight-regular mt-1" style="opacity: 0.95; line-height: 1.4;">
                  <v-icon small color="white" class="mr-1">mdi-asterisk</v-icon>
                  Passport attachment is mandatory
                </div>
              </div>
              <v-spacer></v-spacer>
              <v-btn color="#EDBE38" elevation="4" class="jci-add-doc-btn" @click.prevent="addDocumentPrompt = true">
                <v-icon left color="#0A0F29">mdi-plus-circle</v-icon>
                <span style="color: #0A0F29; font-weight: 700;">Add Document</span>
              </v-btn>
            </v-card-title>
            <v-card-text class="pa-6">
              <!-- Important Passport Validity Note -->
              <v-alert border="left" colored-border color="#EDBE38" elevation="1" class="mb-4" dense>
                <div class="d-flex align-center">
                  <v-icon color="#EDBE38" class="mr-2">mdi-information-outline</v-icon>
                  <span style="color: #0A0F29; font-weight: 500;">
                    <strong>Important:</strong> Your passport must be valid for at least six months from the event date.
                  </span>
                </div>
              </v-alert>

              <!-- Loading State -->
              <div v-if="DocumentLoadingFlag" class="text-center py-4">
                <v-progress-circular indeterminate color="#0097D7" size="40"></v-progress-circular>
              </div>

              <!-- No Documents -->
              <v-alert v-if="!DocumentLoadingFlag && DocumentData.length === 0" border="left" colored-border
                color="#EDBE38" elevation="1">
                <div class="d-flex align-center">
                  <v-icon class="mr-2" color="#EDBE38">mdi-alert-circle-outline</v-icon>
                  <span style="color: #0A0F29;">No documents added yet. Please add your documents (Passport, ID proof,
                    etc.)</span>
                </div>
              </v-alert>

              <!-- Document List -->
              <v-container class="py-0" v-if="!DocumentLoadingFlag && DocumentData.length > 0">
                <v-row>
                  <v-col cols="12" md="6" lg="4" v-for="(item, index) in DocumentData" :key="index">
                    <v-card class="document-card-jci animate-hover" outlined hover>
                      <v-card-text class="pa-3">
                        <div class="d-flex align-start">
                          <v-avatar size="70" tile rounded class="mr-3">
                            <v-img :src="item.ImagePath" :alt="item.DocumentName" contain>
                              <template v-slot:placeholder>
                                <v-row class="fill-height ma-0" align="center" justify="center">
                                  <v-icon>mdi-file-image</v-icon>
                                </v-row>
                              </template>
                            </v-img>
                          </v-avatar>
                          <div class="flex-grow-1">
                            <div class="d-flex align-center justify-space-between mb-1">
                              <h4 class="document-title mb-0">
                                {{ item.DocumentName }}
                              </h4>
                              <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                  <v-btn icon x-small color="error" @click="deleteDocumentAlert(item)" v-bind="attrs"
                                    v-on="on">
                                    <v-icon small>mdi-delete</v-icon>
                                  </v-btn>
                                </template>
                                <span>Delete Document</span>
                              </v-tooltip>
                            </div>
                            <div class="text-caption text--secondary mb-1">
                              {{ item.ReferenceNo }}
                            </div>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
          <v-dialog v-model="PreviewPlanFlag" max-width="600">
            <event-plan-details :Plan="PreviewPlanDetails" @hideDialog="hidePreviewPlanPopup($event)"
              v-if="PreviewPlanFlag"></event-plan-details>
          </v-dialog>

          <!-- Add Document Dialog -->
          <v-dialog v-model="addDocumentPrompt" persistent max-width="800">
            <add-new-document pageDescription="Add New Document" aspacFlag="true" :addRecordPrompt="addDocumentPrompt"
              :MemberId="selectedMember.MemberId" @hideAddRecordPrompt="handleDocumentAdded($event)"
              v-if="addDocumentPrompt"></add-new-document>
          </v-dialog>

          <!-- Pricing Plans Section -->
          <div class="plans-section-header mt-8 mb-6" v-if="selectedMember.MemberId > 0">
            <h2 class="plans-title">
              <v-icon large color="#3A67B1" class="mr-2">mdi-ticket-percent</v-icon>
              <span style="color: #0A0F29;">Choose Your Plan</span>
            </h2>
            <p class="plans-subtitle" style="color: #414042;">Select the best plan that fits your needs</p>
          </div>

          <v-row justify="center">
            <v-col cols="12" sm="6" md="4" v-for="(item, index) in Plans" :key="index">
              <div v-if="item.PlanVisibleFlag">
                <!-- Popular/Recommended Plan -->
                <div v-if="item.Recommended" class="plan-card-wrapper animate-scale-up">
                  <div class="plan-card-jci popular-plan-jci elevation-8">
                    <div class="popular-badge-jci">
                      <v-chip color="#56BDA3" text-color="white" small>
                        <v-icon left small>mdi-star</v-icon>
                        Most Popular
                      </v-chip>
                    </div>

                    <div class="plan-header-jci">
                      <h3 class="plan-title-jci white--text">
                        {{ item.PlanName }}
                      </h3>
                    </div>

                    <div class="plan-body-jci">
                      <div class="plan-price-jci">
                        <div class="price-amount-jci">
                          {{ item.TotalAmountTxt }}
                        </div>
                        <div class="price-period-jci">per head</div>
                      </div>

                      <v-divider class="mb-4"></v-divider>

                      <!-- Action Buttons -->
                      <div class="mt-auto">
                        <v-btn color="#3A67B1" text block large class="mb-3 view-details-btn-jci"
                          @click.prevent="showPreviewPlanPopup(item)">
                          <v-icon left size="20" color="#3A67B1">mdi-information-outline</v-icon>
                          <span style="color: #3A67B1;">View Details</span>
                        </v-btn>

                        <v-btn color="#3A67B1" block x-large elevation="2" :disabled="!valid1" :loading="SubmitFlag"
                          @click.prevent="confirmAlert(item)" class="select-plan-btn-jci jci-btn-navy">
                          <v-icon left size="22" color="white">mdi-check-circle-outline</v-icon>
                          <span style="color: white;">Select This Plan</span>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Regular Plan -->
                <div v-else class="plan-card-wrapper animate-scale-up">
                  <div class="plan-card-jci elevation-4">
                    <div class="plan-header-jci-aqua">
                      <h3 class="plan-title-jci white--text">
                        {{ item.PlanName }}
                      </h3>
                      <p class="plan-subtitle-jci white--text" v-if="item.PlanDescription">
                        {{ item.PlanDescription }}
                      </p>
                    </div>

                    <div class="plan-body-jci">
                      <div class="plan-price-jci">
                        <div class="price-amount-jci" style="color: #0097D7;">
                          {{ item.TotalAmountTxt }}
                        </div>
                        <div class="price-period-jci">per head</div>
                      </div>

                      <v-divider class="mb-4"></v-divider>

                      <!-- Action Buttons -->
                      <div class="mt-auto">
                        <v-btn color="#0097D7" text block large class="mb-3 view-details-btn-jci"
                          @click.prevent="showPreviewPlanPopup(item)">
                          <v-icon left size="20" color="#0097D7">mdi-information-outline</v-icon>
                          <span style="color: #0097D7;">View Details</span>
                        </v-btn>

                        <v-btn color="#0097D7" block x-large elevation="2" :disabled="!valid1" :loading="SubmitFlag"
                          @click.prevent="confirmAlert(item)" class="select-plan-btn-jci jci-btn-aqua">
                          <v-icon left size="22" color="white">mdi-check-circle-outline</v-icon>
                          <span style="color: white;">Select This Plan</span>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-container>
      <br /><br />
    </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import AddSmaNonMember from "@/view/pages/erp/events/sma-natcon/AddSmaNonMember.vue";
import EventPlanDetails from "@/view/pages/erp/events/natcon/EventPlanDetails.vue";
import AddNewDocument from "@/view/pages/profile/my-profile/AddNewDocument";

export default {
  mixins: [common],
  components: {
    AddSmaNonMember,
    EventPlanDetails,
    AddNewDocument,
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      StartupLoadingFlag: false,
      ProgressLoadingFlag: false,
      SubmitFlag: false,
      SearchFlag: false,
      NotesFlag: false,
      rows: [],
      tableColumns1: [],

      AspacFlag: true,

      PreviewPlanFlag: false,
      PreviewPlanDetails: {},

      addDocumentPrompt: false,
      DocumentLoadingFlag: false,
      DocumentData: [],
      selectedDocument: {},

      selected: [],
      search: "",

      MemberPaymentLink: "",
      RegisterDetailsTxt: "Member Details",

      RegistrationTypeId: "",

      Qty: "",
      QtyFlag: false,
      QtyRules: [
        (v) => Number.isInteger(Number(v)) || "Nos is invalid",
        (v) => v > 0 || "Number must be greater than zero",
        (v) => v < 7 || "Number must be less than 6",
      ],

      MemberType: "",
      MemberTypeRules: [(v) => !!v || "Member type is required"],
      MemberTypeOptions: [
        { value: "", text: "--Select Type--" },
        { value: 1, text: "For me" },
        { value: 2, text: "For another member" },
      ],

      Gender: "",
      GenderRules: [(v) => !!v || "Gender is required"],
      GenderOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Male" },
        { value: 2, text: "Female" },
        { value: 3, text: "Other" },
      ],

      FoodPreference: "",
      FoodPreferenceRules: [(v) => !!v || "Food preference is required"],
      FoodPreferenceOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Veg" },
        { value: 2, text: "Non Veg" },
      ],

      MemberTypeId: 1,

      Lom: {},

      Member: {},

      Presidentmember: {},
      Plans: [],

      selectedPlan: {},
      selectedMember: {},

      MobileNoRules: [(v) => !!v || "Mobile No is required"],

      EmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      TshirtSize: "",
      TshirtSizeRules: [(v) => !!v || "T-Shirt size is required"],
      TshirtSizeOptions: [
        { value: "", text: "--Select--" },
        { value: "S", text: "S" },
        { value: "M", text: "M" },
        { value: "L", text: "L" },
        { value: "XL", text: "XL" },
        { value: "XXL", text: "XXL" },
        { value: "3XL", text: "3XL" },
        { value: "4XL", text: "4XL" },
        { value: "5XL", text: "5XL" },
      ],

      PassportNumber: "",
      PassportNumberRules: [(v) => !!v || "Passport number is required"],

      SenatorNumber: "",
      SenatorNumberRules: [],

      FirstTimer: "",
      FirstTimerRules: [(v) => !!v || "First timer status is required"],
      FirstTimerOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Yes" },
        { value: 2, text: "No" },
      ],

      JvcEmailId: "",
      JvcEmailIdRules: [
        (v) => !!v || "JVC Email ID is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      CompanyName: "",
      CompanyNameRules: [(v) => !!v || "Company/Organization name is required"],

      Department: "",
      DepartmentRules: [
        (v) => !!v || "Section/Division/Department is required",
      ],

      JobTitle: "",
      JobTitleRules: [(v) => !!v || "Job title is required"],

      Allergies: "",
      AllergiesRules: [
        (v) => !!v || "Please declare any known allergies or write 'None'",
      ],

      MedicalConditions: "",
      MedicalConditionsRules: [
        (v) => !!v || "Please declare any medical conditions or write 'None'",
      ],

      Events: {},

      MemberIdRules: [(v) => !!v || "Member is required"],
      MemberId: "",
      MemberIdOptions: [],
      MemberIdOptionsLoading: false,

      CheckoutTableHeaders: [],

      tab: null,
      TabDisable1: false,
      TabDisable2: false,

      TotalAmount: 0,
      Contribution: [],

      TotalAmount1: 0,
      TotalAmount2: 0,

      Member: {},
      Order: {},
      ThemeConfig: {},
      MemberImageFlag: false,

      PenaltySubscription: [],

      direction: "top",
      fab: true,
      fling: false,
      hover: true,
      tabs: null,
      top: false,
      right: true,
      bottom: true,
      left: false,
      transition: "slide-y-reverse-transition",

      OrderType: 1,

      JciYearCodeRules: [(v) => !!v || "Year is required"],
      JciYearCodeOptions: [],
      JciYearCodeOptionsLoading: false,

      MobileNoRules: [(v) => !!v || "Mobile No is required"],

      EmailIdRules: [
        (v) => !!v || "E-mail is required",
        (v) => /.+@.+\..+/.test(v) || "E-mail must be valid",
      ],

      GstNumberFlag: 2,
      GstNumberFlagRules: [],
      GstNumberFlagOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "Yes" },
        { value: 2, text: "No" },
      ],

      GstNumber: "",
      GstNumberRules: [(v) => !!v || "GST number is required"],

      ZoneCode: "",
      LomCode: "",
      MemberId: "",
      UserMemberId: "",

      EventId: "",
      EventTypeId: 16,

      addNonMemberRecordPrompt: false,
      ParticipantMaxLimit: 6,
      AllParticipants: [],
      NonMemberDetails: {},
      uploadedParticipantImages: [],
      tableColumns2: [
        { text: "Participant Name", value: "ParticipantName" },
        { text: "Gender", value: "Gender" },
        { text: "Age", value: "Age" },
        { text: "Mobile number", value: "MobileNo" },
        { text: "Email Id", value: "EmailId" },
        { text: "Food", value: "Food" },
        { text: "Action", value: "action" },
      ],

      Order: {
        back_url: "",
        customer_name: "",
        customer_email_id: "",
        customer_mobile_no: "",
        rzp_action: "#",
        rzp_account_id: "",
        rzp_key: "bdgzbd",
        rzp_amount: "",
        rzp_order_id: "",
        rzp_buttontext: "Pay now",
        rzp_name: "JCI India Foundation",
        rzp_description: "",
        rzp_image: "",
        rzp_color: "#0096d5",
      },

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      RoleId: "",
    };
  },
  computed: {},
  mounted() {
    /*
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: "Vuetify", route: "alerts" },
      { title: "Form Inputs & Control", route: "autocompletes" },
      { title: "Fileinptus" },
    ]);
    */
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = CurrentJciYearId;

        var UserMemberId = this.$session.get("MemberId");
        this.UserMemberId = UserMemberId;
        console.log({ UserMemberId });

        var ZoneId = this.$session.get("ZoneId");
        this.ZoneCode = ZoneId;
        console.log({ ZoneId });

        var LomId = this.$session.get("LomId");
        this.LomCode = LomId;
        console.log({ LomId });

        var RoleId = this.$session.get("RoleId");
        RoleId = RoleId == (null || undefined) ? "" : RoleId;
        console.log({ RoleId });
        this.RoleId = RoleId;
        var MemberPaymentLink =
          RoleId == 9
            ? "/natcon/payment/create"
            : "/natcon/member-payment/create";
        console.log({ MemberPaymentLink });
        this.MemberPaymentLink = MemberPaymentLink;

        var AspacFlag = this.AspacFlag;
        console.log({ AspacFlag });

        if (AspacFlag) {
          var MemberTypeOptions = [
            { value: "", text: "--Select Type--" },
            { value: 1, text: "For me" },
          ];
        } else {
          if (RoleId == 9) {
            var MemberTypeOptions = [
              { value: "", text: "--Select Type--" },
              { value: 2, text: "For member" },
              { value: 3, text: "For non member" },
            ];
          } else {
            var MemberTypeOptions = [
              { value: "", text: "--Select Type--" },
              { value: 1, text: "For me" },
              { value: 2, text: "For another member" },
              { value: 3, text: "For non member" },
            ];
          }
        }
        console.log({ MemberTypeOptions });
        this.MemberTypeOptions = MemberTypeOptions;

        this.pageData();
      }
    },
    JciYearCodeOptions: function () {
      console.log("watch JciYearCodeOptions");
      this.LoadingFlag = false;
      this.JciYearCodeOptionsLoading = false;
    },
    Qty: function () {
      console.log("watch Qty");
      var Qty = this.Qty;
      Qty = Qty == (null || undefined || isNaN(Qty)) ? 0 : parseFloat(Qty);
      console.log({ Qty });
      var Plans = this.Plans;
      console.log({ Plans });
      var newPlans = [];
      Plans.forEach((item) => {
        console.log({ item });
        var temp = item;
        var Amount = parseFloat(item.SubTotalAmount);
        console.log({ Amount });
        var TotalAmount = Qty * Amount;
        TotalAmount =
          TotalAmount == (null || undefined || isNaN(TotalAmount))
            ? 0
            : parseFloat(TotalAmount);
        console.log({ TotalAmount });
        temp.Qty = Qty;
        temp.TotalAmount = TotalAmount;
        newPlans.push(temp);
      });
      this.Plans = newPlans;
    },
    MemberIdOptions: function () {
      this.MemberIdOptionsLoading = false;
    },
    MemberType: function () {
      console.log("watch MemberType");
      this.selectedMember = {};
      var MemberType = this.MemberType;
      console.log("MemberType=" + MemberType);
      if (MemberType != "") {
        this.AllParticipants = [];
        this.Qty = 0;
        var Member = this.Member;
        console.log({ Member });
        var MemberId = Member.MemberId;
        console.log({ MemberId });
        var tempData = {
          MemberFlag: true,
          MemberId: MemberId,
          MembershipId: Member.MembershipId,
          MemberName: Member.Fullname,
          Gender: "",
          Age: "",
          MobileNo: Member.MobileNo,
          EmailId: Member.EmailId,
          Gender: Member.Gender,
          FoodChoice: Member.FoodChoice,
          MemberImagePath: Member.MemberImage,
          action: "",
        };
        console.log({ tempData });
        if (MemberType == 1) {
          this.RegisterDetailsTxt = "Member Details";
          // this.AllParticipants.push(tempData);
          this.selectedMember = tempData;
          this.Qty = 1;
          this.MemberId = MemberId;
          this.getDocumentDetails();
        } else if (MemberType == 2) {
          this.MemberId = "";
          this.RegisterDetailsTxt = "Member Details";
          this.getSelectedMemberDetails();
        } else {
          this.RegisterDetailsTxt = "Payee Details";
          this.selectedMember = tempData;
          this.MemberId = MemberId;
          this.getDocumentDetails();
        }
      }
    },
  },
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      // this.resetForm();
      var id = this.$route.query.id;
      console.log({ id });
      this.EventId = id;
      var url1 = "api/jci-program/show";
      var condition1 = {
        UserInterface: 1,
        Program: id,
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
    },
    pageData() {
      console.log("pageData called");
      this.getMemberDetails();
      this.getMemberIdOptions();
      this.getEventsDetails();
      this.getDocumentDetails();
    },
    // code 1
    validate() {
      if (this.$refs.form2.validate()) {
        this.snackbar = true;
      }
    },
    addRecord() {
      console.log("addRecord called");
      var url = "/staff/list";
      var params = {};
      this.pageRedirect(params, url);
    },
    resetForm() {
      this.$refs.form2.reset();
      this.selectedMember = {};
      this.selectedPlan = {};
      // this.refreshPageData();
    },
    resetValidation() {
      this.$refs.form2.resetValidation();
    },
    openNonMemberDialog() {
      console.log("openNonMemberDialog called");
      var n2 = this.AllParticipants.length;
      console.log({ n2 });
      var m2 = this.ParticipantMaxLimit;
      console.log({ m2 });
      if (n2 < m2) {
        this.addNonMemberRecordPrompt = true;
      } else {
        var message = "Participant maximum limit " + m2 + " reached. ";
        this.sweetAlert("error", message, false);
      }
    },
    showPreviewPlanPopup(tr) {
      this.PreviewPlanDetails = tr;
      this.PreviewPlanFlag = true;
    },
    hidePreviewPlanPopup() {
      this.PreviewPlanDetails = {};
      this.PreviewPlanFlag = false;
    },
    setQty() {
      console.log("setQty called");
      var n3 = this.AllParticipants.length;
      console.log({ n3 });
      this.Qty = n3;
    },
    addNewNonMember(member) {
      console.log("addNewNonMember called");
      this.addNonMemberRecordPrompt = false;
      console.log({ member });
      this.NonMemberDetails = member;
      this.addNewRow(1);
    },
    addNewRow(tableId) {
      console.log("addNewRow called");

      console.log("tableId=" + tableId);

      switch (tableId) {
        case 1:
          var n2 = this.AllParticipants.length;
          console.log({ n2 });
          var MemberFlag = this.NonMemberDetails.MemberFlag;
          MemberFlag = MemberFlag == (null || undefined) ? false : MemberFlag;
          console.log({ MemberFlag });
          var m2 = this.ParticipantMaxLimit;
          console.log({ m2 });
          if (n2 < m2) {
            if (MemberFlag) {
              this.AllParticipants.push(this.NonMemberDetails);
              this.setQty();
            }
          } else {
            var message = "Participant maximum limit " + m2 + " reached. ";
            this.sweetAlert("error", message, false);
          }
          this.NonMemberDetails = {};
          break;

        default:
          break;
      }
    },
    deleteRow(tableId, index, tr) {
      console.log("deleteRow called");
      console.log({ tableId });
      console.log({ index });
      console.log({ tr });
      if (index >= 0) {
        switch (tableId) {
          case 1:
            this.AllParticipants.splice(index, 1);
            this.setQty();
            break;

          default:
            break;
        }
      }
    },
    getMemberIdOptions() {
      console.log("getMemberIdOptions called");
      var RoleId = this.RoleId;
      console.log({ RoleId });
      var UserInterface = RoleId == 9 ? 4 : 2;
      console.log({ UserInterface });
      var selectbox1_source = "MemberId";
      var selectbox1_destination = "MemberIdOptions";
      var selectbox1_url = "api/members/options";
      var selectbox1_conditions_array = {
        UserInterface: 2,
        LomCode: this.LomCode,
        MemberType: this.MemberTypeId,
        MemberTypeFlag: true,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getSelectedMemberDetails() {
      console.log("getSelectedMemberDetails called");
      this.selectedMember = {};
      this.AllParticipants = [];
      var temp_code = this.MemberId;
      console.log("temp_code=" + temp_code);
      var idx = this.MemberIdOptions.map((e) => e.value).indexOf(temp_code);
      console.log("idx=" + idx);
      if (idx > 0) {
        var member = this.MemberIdOptions[idx];
        console.log("member=" + JSON.stringify(member));
        var tempData = {
          MemberFlag: true,
          MemberId: member.value,
          MembershipId: member.membership_id,
          MemberName: member.member_name,
          Gender: member.gender,
          FoodChoice: member.food_choice,
          Age: "",
          MobileNo: "",
          EmailId: "",
          MemberImagePath: member.photo,
          action: "",
        };
        // this.AllParticipants.push(tempData);
        this.selectedMember = tempData;
        this.Qty = 1;
        this.getDocumentDetails();
      } else {
        this.Qty = 0;
      }
    },
    getDocumentDetails() {
      console.log("getDocumentDetails called");

      var memberId = this.selectedMember.MemberId;
      console.log({ memberId });

      var MemberId = memberId > 0 ? memberId : "";
      console.log({ MemberId });

      if (MemberId == "") {
        console.log("No MemberId available");
        return;
      }

      this.DocumentLoadingFlag = true;
      this.DocumentData = [];

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/members-document-details/show";
      var upload = {
        UserInterface: 2,
        MemberId: MemberId,
      };

      console.log("upload=" + JSON.stringify(upload));

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.DocumentLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          records = response.data.records;
          console.log("output=" + output + ", flag=" + flag);

          if (flag == 1) {
            thisIns.DocumentData = records.TableData || [];
            console.log(thisIns.DocumentData);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.DocumentLoadingFlag = false;
        });
    },
    handleDocumentAdded(flag) {
      console.log("handleDocumentAdded called, flag=" + flag);
      this.addDocumentPrompt = false;
      if (flag) {
        this.getDocumentDetails();
        this.toast("success", "Document added successfully");
      }
    },
    deleteDocumentAlert(tr) {
      console.log("deleteDocumentAlert called");
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.selectedDocument = tr;
        this.confirmDeleteDocument();
      } else {
        this.toast("error", "Kindly select one document to delete");
      }
    },
    confirmDeleteDocument() {
      console.log("confirmDeleteDocument called");
      var tr = this.selectedDocument;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        var htmlTxt = "";
        htmlTxt += "Document name <b> " + tr.DocumentName + "</b>";
        htmlTxt += "<br/> Reference No <b> " + tr.ReferenceNo + "</b>";
        htmlTxt +=
          "<br/><img width='100' height='100' src='" + tr.ImagePath + "' />";
        htmlTxt += "<br/><br/>Do you want to delete this document?";
        Swal.fire({
          title: "Are you sure?",
          text: "Do you want to delete this document?",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Delete`,
          confirmButtonColor: "#d33",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#3085d6",
        }).then((result) => {
          if (result.isConfirmed) {
            this.deleteDocument();
          } else {
            this.toast("error", "You cancelled the process");
          }
        });
      } else {
        this.toast("error", "Kindly select one document to delete");
      }
    },
    deleteDocument() {
      console.log("deleteDocument called");

      var tr = this.selectedDocument;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.DocumentLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members-document-details/destroy";
        var upload = {
          UserInterface: 1,
          Member: tr.MemberId,
          Document: tr.DocumentId,
        };
        console.log("upload=" + JSON.stringify(upload));

        const thisIns = this;
        var output = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.DocumentLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            if (flag == 1) {
              thisIns.selectedDocument = {};
              thisIns.getDocumentDetails();
              thisIns.toast("success", output);
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.DocumentLoadingFlag = false;
          });
      } else {
        this.toast("error", "Kindly select one document to delete");
      }
    },
    getMemberDetails() {
      console.log("getMemberDetails is called");

      var MemberId = this.UserMemberId;
      var ZoneCode = this.ZoneCode;
      var LomCode = this.LomCode;
      console.log(
        "MemberId=" +
          MemberId +
          "; ZoneCode=" +
          ZoneCode +
          "; LomCode=" +
          LomCode
      );

      if (MemberId != "" && ZoneCode != "" && LomCode != "") {
        this.LoadingFlag = true;
        this.Member = {};

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/members/search";
        var upload = {
          UserInterface: 4,
          MemberId: MemberId,
          ZoneCode: ZoneCode,
          LomCode: LomCode,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.LoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Member = records;
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.LoadingFlag = false;
          });
      } else {
        var message = "";
        if (MemberId == "" || ZoneCode == "" || LomCode == "") {
          message += "Your session is expired. Kindly login again. ";
        }
        this.toast("error", message);
      }
    },
    getEventsDetails() {
      console.log("getEventsDetails is called");

      var EventCode = this.EventId;
      console.log("EventCode=" + EventCode);

      if (EventCode != "") {
        this.SubmitFlag = true;
        this.StartupLoadingFlag = true;
        this.Events = {};
        this.Plans = [];

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/plan/lists";
        var upload = {
          UserInterface: 2,
          Program: EventCode,
          ProgramType: this.EventTypeId,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            console.log("output=" + output + ", flag=" + flag);

            records = response.data.records;
            console.log({ records });

            if (flag == 1) {
              thisIns.Plans = records.TableData;
              thisIns.setQty();
              // thisIns.toast("success", output);
              // thisIns.resetForm();
            } else {
              thisIns.toast("error", output);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.StartupLoadingFlag = false;
          });
      } else {
        var message = "";
        if (EventCode == "") {
          message += "Event Id should not be empty ";
        }
        this.toast("error", message);
      }
    },
    confirmAlert(selectedPlan) {
      console.log("confirmAlert called");

      var validate1 = this.$refs.form2.validate();
      console.log({ validate1 });

      var selectedMember = this.selectedMember;
      console.log({ selectedMember });

      var MemberType = this.MemberType;
      console.log({ MemberType });

      var RegistrationTypeId = MemberType <= 2 ? 3 : 4;
      console.log({ RegistrationTypeId });

      var MemberId = selectedMember.MemberId;
      MemberId = MemberId == (null || undefined) ? "" : MemberId;
      console.log({ MemberId });

      this.selectedPlan = selectedPlan;
      console.log({ selectedPlan });

      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log({ EventPlanId });

      var participants = this.AllParticipants;
      console.log({ participants });

      var TotalCount = this.Qty;
      console.log("TotalCount=" + TotalCount);

      var EventTotalAmount = selectedPlan.SubTotalAmount;
      console.log("EventTotalAmount=" + EventTotalAmount);

      var TotalAmount = parseFloat(TotalCount) * parseFloat(EventTotalAmount);
      console.log("TotalAmount=" + TotalAmount);

      if (validate1 && TotalCount > 0 && TotalAmount > 0 && MemberType > 0) {
        this.RegistrationTypeId = RegistrationTypeId;

        var MemberTxt = "";
        participants.forEach((item) => {
          console.log({ item });
          MemberTxt +=
            "<br/>" +
            item.ParticipantName +
            " &nbsp; - " +
            item.MobileNo +
            " &nbsp; - " +
            item.EmailId +
            " &nbsp; - " +
            item.Food;
        });
        var htmlTxt = "";
        htmlTxt += "<br/><b>Selected plan details</b>";
        htmlTxt += "<br/>Plan name <b>" + selectedPlan.PlanName + "</b>";
        htmlTxt += "<br/><br/><b>Selected participant details</b>";

        if (RegistrationTypeId == 3) {
          htmlTxt +=
            "<br/>Member Name <b>" + selectedMember.MemberName + "</b>";
          htmlTxt += "<br/>Mobile no. <b>" + selectedMember.MobileNo + "</b>";
          htmlTxt += "<br/>Email id <b>" + selectedMember.EmailId + "</b>";
          htmlTxt +=
            "<br/><img width='100' height='100' src='" +
            selectedMember.MemberImagePath +
            "' />";
        } else {
          htmlTxt +=
            "<br/><b> Member Name &nbsp; - Mobile no. &nbsp; - Email id &nbsp; - Food </b>";
          htmlTxt += MemberTxt;
        }

        htmlTxt += "<br/><br/>Total amount <b> ₹ " + TotalAmount + "</b>";
        htmlTxt += "<br/><br/>Do you want to continue?";
        Swal.fire({
          title: "Please verify all the information before proceeding",
          text: "",
          icon: "warning",
          html: htmlTxt,
          showCancelButton: true,
          confirmButtonText: `Continue`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
            // this.makePayment();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (MemberId == "") {
          message += "Kindly select member. ";
        }
        if (EventPlanId == "") {
          message += "Kindly select plan. ";
        }
        if (TotalCount == 0) {
          message += "Kindly add atleast one participant. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      var validate1 = this.$refs.form2.validate();
      console.log({ validate1 });

      var selectedPlan = this.selectedPlan;
      console.log({ selectedPlan });

      var selectedMember = this.selectedMember;
      console.log({ selectedMember });

      var MemberType = this.MemberType;
      console.log({ MemberType });

      var RegistrationTypeId = this.RegistrationTypeId;
      console.log({ RegistrationTypeId });

      var MemberId = selectedMember.MemberId;
      MemberId = MemberId == (null || undefined) ? "" : MemberId;
      console.log({ MemberId });

      var EventPlanId = selectedPlan.EventPlanId;
      EventPlanId = EventPlanId == (null || undefined) ? "" : EventPlanId;
      console.log({ EventPlanId });

      var participants = this.AllParticipants;
      console.log({ participants });

      var TotalCount = this.Qty;
      console.log("TotalCount=" + TotalCount);

      var EventTotalAmount = selectedPlan.TotalAmount;
      console.log("EventTotalAmount=" + EventTotalAmount);

      var TotalAmount = parseFloat(TotalCount) * parseFloat(EventTotalAmount);
      console.log("TotalAmount=" + TotalAmount);

      if (
        validate1 &&
        TotalCount > 0 &&
        TotalAmount > 0 &&
        RegistrationTypeId > 0
      ) {
        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/jci-program/order/create";
        var upload = {
          UserInterface: RegistrationTypeId,
          RegistrationType: RegistrationTypeId,
          Lom: this.LomCode,
          Member: MemberId,
          Event: this.EventId,
          Plan: EventPlanId,
          MobileNo: selectedMember.MobileNo,
          EmailId: selectedMember.EmailId,
          Gender: MemberType == 3 ? null : selectedMember.Gender,
          FoodPreference: MemberType == 3 ? null : selectedMember.FoodChoice,
          GstNumberFlag: selectedMember.GstNumberFlag,
          GstNumber:
            selectedMember.GstNumberFlag == 1 ? selectedMember.GstNumber : null,
          TshirtSize: this.TshirtSize,
          PassportNumber: this.PassportNumber,
          SenatorNumber: this.SenatorNumber,
          FirstTimer: this.FirstTimer,
          JvcEmailId: this.JvcEmailId,
          CompanyName: this.CompanyName,
          Department: this.Department,
          JobTitle: this.JobTitle,
          Allergies: this.Allergies,
          MedicalConditions: this.MedicalConditions,
          TotalCount: TotalCount,
          TotalAmount: TotalAmount,
          Participants: participants,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        this.SubmitFlag = true;
        this.ProgressLoadingFlag = true;

        // this.$session.set("checkout", upload);
        // this.checkout();

        const thisIns = this;
        var output = "";
        var records = "";
        var successTxt = "";
        var errorTxt = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            // thisIns.pageLoadingOff()
            thisIns.SubmitFlag = false;
            thisIns.ProgressLoadingFlag = false;

            output = response.data.output;
            flag = response.data.flag;
            output = response.data.output;
            records = response.data.records;
            console.log("output=" + output + ", flag=" + flag);
            successTxt = response.data.success;
            errorTxt = response.data.error;
            console.log("successTxt=" + successTxt + ", errorTxt=" + errorTxt);

            if (flag == 1) {
              /*
              thisIns.sweetAlert(
                "info",
                "You will be redirected to checkout page shortly",
                true
              );
              */
              thisIns.Order = records.Order;
              thisIns.ThemeConfig = records.Theme;
              var PaymentGateway = records.PaymentGateway;
              if (PaymentGateway == 2) {
                thisIns.billdeskPayment();
              } else {
                thisIns.makePayment();
              }
            } else {
              // thisIns.toast("error", output);
              // thisIns.Order = records.Order;
              // thisIns.makePayment();
              thisIns.sweetAlert("info", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.SubmitFlag = false;
            thisIns.ProgressLoadingFlag = false;
          });
      } else {
        var message = "";
        if (!validate1) {
          message += "Kindly fill the required fields. ";
        }
        if (TotalCount == 0) {
          message += "Kindly add atleast one participant. ";
        }
        if (TotalAmount == 0) {
          message += "Kindly select a plan. ";
        }
        this.sweetAlert("error", message, false);
      }
    },
    billdeskPayment() {
      console.log("billdeskPayment called");
      const thisIns = this;
      var responseHandler = function (txn) {
        console.log({ txn });

        var response = txn.txnResponse;
        console.log({ response });

        var message = response.hasOwnProperty("message")
          ? response.message
          : "";
        console.log({ message });

        var transactionResponse = response.hasOwnProperty(
          "transaction_response"
        )
          ? response.transaction_response
          : "";
        console.log({ transactionResponse });

        if (transactionResponse != "") {
          thisIns.SubmitFlag = true;
          // thisIns.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
          thisIns.pageRedirect({}, "/order/success");
        } else {
          thisIns.sweetAlert("error", message, true);
        }
      };

      var config = {
        responseHandler: responseHandler,
        flowConfig: this.Order,
        flowType: "payments",
        themeConfig: this.ThemeConfig,
      };

      window.loadBillDeskSdk(config);
    },
    makePayment() {
      console.log("makePayment");
      // ev.preventDefault();
      var order = this.Order;
      console.log({ order });
      const thisIns = this;
      var rzp1 = new Razorpay({
        key: order.rzp_key,
        account_id: order.rzp_account_id,
        order_id: order.rzp_order_id,
        amount: order.rzp_amount,
        name: order.rzp_name,
        currency: "INR",
        description: order.rzp_description,
        image: order.rzp_image,
        theme: {
          color: order.rzp_color,
        },
        prefill: {
          name: order.customer_name,
          email: order.customer_email_id,
          contact: order.customer_mobile_no,
        },
        notes: {
          address: "",
        },
        // callback_url: "http://domain.com/#/about/" + this.order_id,
        redirect: false,
        handler: function (response) {
          console.log("razorpay_payment_id=" + response.razorpay_payment_id);
          thisIns.SubmitFlag = true;
          // thisIns.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
          thisIns.pageRedirect({}, "/order/success");
        },
      });
      rzp1.open();
    },
  },
  beforeMount() {
    this.refreshPageData();
    /*
    this.tableColumns1.push(
      { text: "Description", value: "Description" },
      { text: "Subscription Fees", value: "AmountTxt" },
      { text: "Nos", value: "Qty" },
      { text: "Sub Total", value: "SubTotal" }
      // { text: "Actions", value: "actions" }
    );
    */
  },
};
</script>
<style lang="scss">
// Color Palette (JCI Approved Colors)
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;

$primary-color: $navy;
$secondary-color: $aqua;
$success-color: $seaform;
$warning-color: $gold;
$danger-color: #e74c3c;
$info-color: $aqua;
$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$text-primary: $text-off-black;
$text-secondary: $text-90-black;
$border-color: #e2e8f0;
$gradient-main: linear-gradient(135deg, $navy 0%, $aqua 100%);

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-12px);
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

#natcon-member-payment {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  .header-card {
    background: linear-gradient(
      135deg,
      $navy 0%,
      $aqua 50%,
      $navy 100%
    ) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5);

    .header-backdrop-effect {
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.15) 0%,
        transparent 70%
      );
      animation: rotate 25s linear infinite;
    }

    .header-content {
      position: relative;
      z-index: 2;
    }
  }

  .header-icon-box {
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3);
    color: $text-white;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.9;
    color: $text-white;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    color: white !important;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      background: rgba(255, 255, 255, 0.25) !important;
    }
  }

  .required-note-card {
    background: linear-gradient(135deg, #3a67b1 0%, #0097d7 100%) !important;
    border-radius: 15px !important;
    padding: 20px;
    color: white;

    .required-note-content {
      display: flex;
      align-items: center;
      gap: 15px;
    }

    .required-note-icon {
      background: rgba(255, 255, 255, 0.2);
      padding: 10px;
      border-radius: 12px;
    }

    .asterisk {
      color: #ffeb3b;
      font-weight: bold;
    }
  }

  .section-header {
    padding: 20px 24px !important;
    display: flex;
    align-items: center;
    gap: 15px;
    font-size: 1.1rem;
    font-weight: 600;

    &.bg-gradient-navy {
      background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    }

    &.bg-gradient-seaform {
      background: linear-gradient(135deg, $seaform 0%, #3d9983 100%) !important;
    }

    &.bg-gradient-aqua {
      background: linear-gradient(135deg, $aqua 0%, #0078c1 100%) !important;
    }

    &.bg-gradient-gold {
      background: linear-gradient(135deg, $gold 0%, #d4a030 100%) !important;
    }
  }

  .form-card {
    border-radius: 20px !important;
    overflow: hidden;
    border: none !important;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05) !important;
    background: white !important;
  }

  .info-card-jci {
    border-radius: 20px !important;
    transition: all 0.3s ease;
    border: 1px solid #f1f5f9 !important;

    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1) !important;
    }
  }

  // Plan Cards
  .plan-card-wrapper {
    padding: 15px;
  }

  .plan-card-jci {
    border-radius: 24px;
    overflow: hidden;
    background: white;
    transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    border: 1px solid #e2e8f0;

    &:hover {
      transform: translateY(-12px) scale(1.02);
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.1) !important;
    }

    .plan-header-jci,
    .plan-header-jci-aqua {
      padding: 30px 24px;
      text-align: center;
      color: white;
      background: linear-gradient(135deg, $navy 0%, $aqua 100%);
    }

    .plan-header-jci-aqua {
      background: linear-gradient(135deg, $aqua 0%, $seaform 100%);
    }

    .plan-body-jci {
      padding: 30px 24px;
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .plan-price-jci {
      text-align: center;
      margin-bottom: 25px;

      .price-amount-jci {
        font-size: 32px;
        font-weight: 800;
        color: $navy;
      }

      .price-period-jci {
        font-size: 14px;
        color: $text-90-black;
        text-transform: uppercase;
        letter-spacing: 1px;
      }
    }
  }

  .popular-plan-jci {
    border: 3px solid $seaform !important;
    position: relative;

    .popular-badge-jci {
      position: absolute;
      top: 15px;
      right: 15px;
      z-index: 10;
    }
  }

  // Field Labels
  .field-label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 8px;
    color: $text-off-black;
  }

  // Animations Classes
  .animate-slide-up {
    animation: slideInUp 0.6s ease-out forwards;
  }

  .animate-scale-up {
    animation: scaleUp 0.5s ease-out forwards;
  }

  @keyframes scaleUp {
    from {
      opacity: 0;
      transform: scale(0.9);
    }

    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  // Custom Table Styles
  .custom-table-jci {
    border-radius: 12px;
    overflow: hidden;

    th {
      background: #f8fafc !important;
      font-weight: 700 !important;
      text-transform: uppercase;
      font-size: 0.75rem !important;
      letter-spacing: 0.5px;
      color: $text-90-black !important;
    }
  }

  // Button Styles
  .jci-btn-navy {
    background: $navy !important;
    color: white !important;
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px;
  }

  .jci-btn-aqua {
    background: $aqua !important;
    color: white !important;
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    letter-spacing: 0.5px;
  }

  .view-details-btn-jci {
    border-radius: 12px !important;
    text-transform: none !important;
    font-weight: 600 !important;
    border: 1px solid currentColor !important;
  }

  .section-subtitle-jci {
    display: flex;
    align-items: center;
    border-bottom: 2px solid #f1f5f9;
    padding-bottom: 8px;
  }
}
</style>
